(() => {
  function initDiscount(root) {
    const offerCode = root.dataset.offerCode || 'AUTUMN20';
    const countryLabel = root.dataset.offerCountryLabel || 'Canada only';
    const apply = root.querySelector('[data-apply-discount]');
    if (!apply) return;
    const label = apply.querySelector('[data-discount-label]');
    const icon = apply.querySelector('[data-discount-icon]');
    const status = root.querySelector('[data-discount-status]');
    const fallbackHref = apply.getAttribute('href');
    const shopRoot = window.Shopify?.routes?.root || '/';
    let applying = false;
    let saved = false;
    let revision = 0;

    async function requestCart(path, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(`${shopRoot}${path}`, {
          ...options, credentials: 'same-origin', cache: 'no-store', signal: controller.signal
        });
        if (!response.ok) throw new Error('Cart request failed');
        return await response.json();
      } finally {
        clearTimeout(timeout);
      }
    }

    function showDiscount(cart) {
      const code = cart.discount_codes?.find(discount => discount.code.toUpperCase() === offerCode);
      saved = Boolean(code);
      label.textContent = code ? `${offerCode} ${code.applicable ? 'applied' : 'saved'}` : `Apply ${offerCode}`;
      icon.textContent = code ? '✓' : '→';
      apply.setAttribute('href', code ? '#planner-products' : fallbackHref);
      status.textContent = !code ? `${countryLabel} · Use ${offerCode} at checkout`
        : code.applicable ? `20% off eligible items in your cart. ${countryLabel}.`
        : cart.total_discount > 0 ? `Code saved. Your cart is using another offer. ${countryLabel}.`
        : `Saved for checkout. Add an eligible planner colour. ${countryLabel}.`;
    }

    async function syncDiscount() {
      if (applying || !root.isConnected) return;
      const current = ++revision;
      try {
        const cart = await requestCart('cart.js');
        if (!applying && current === revision && root.isConnected) showDiscount(cart);
      } catch {
        // Keep the working Shopify discount link if the cart cannot be read.
      }
    }

    apply.addEventListener('click', async event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (saved && !applying) return;
      event.preventDefault();
      if (applying) return;
      applying = true;
      revision++;
      apply.setAttribute('aria-busy', 'true');
      apply.setAttribute('aria-disabled', 'true');
      label.textContent = `Applying ${offerCode}…`;
      status.textContent = 'Saving your code…';
      try {
        const before = await requestCart('cart.js');
        const codes = new Map((before.discount_codes || []).map(discount => [discount.code.toUpperCase(), discount.code]));
        codes.set(offerCode, offerCode);
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true, detail: { sections }
        }));
        const bundledSections = [...new Set(sections)].slice(0, 5);
        const cart = await requestCart('cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: [...codes.values()].join(','), sections: bundledSections, sections_url: location.pathname })
        });
        if (bundledSections.length && bundledSections.every(section => typeof cart.sections?.[section] === 'string')) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true, detail: { baseEvent: 'discount:update', cart }
          }));
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        if (!cart.discount_codes?.some(discount => discount.code.toUpperCase() === offerCode)) throw new Error('Code not saved');
        showDiscount(cart);
      } catch {
        saved = false;
        label.textContent = `Apply ${offerCode}`;
        icon.textContent = '→';
        apply.setAttribute('href', fallbackHref);
        status.textContent = `Couldn’t save your code. Try again or enter ${offerCode} at checkout.`;
      } finally {
        applying = false;
        apply.removeAttribute('aria-busy');
        apply.removeAttribute('aria-disabled');
      }
    });

    document.addEventListener('cart:change', event => {
      if (applying || !root.isConnected) return;
      if (Array.isArray(event.detail?.cart?.discount_codes)) {
        revision++;
        showDiscount(event.detail.cart);
      } else {
        syncDiscount();
      }
    });
    document.addEventListener('cart:refresh', syncDiscount);
    window.addEventListener('pageshow', event => { if (event.persisted) syncDiscount(); });
    syncDiscount();
  }

  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    initDiscount(root);
    const cards = [...root.querySelectorAll('[data-card]')];
    const groups = [...root.querySelectorAll('[data-cover-group]')];
    const filters = [...root.querySelectorAll('[data-filter]')];
    const controls = root.querySelector('[data-catalog-filters]');
    const toggle = root.querySelector('[data-filter-toggle]');
    const toolbar = root.querySelector('[data-catalog-toolbar]');
    const sort = root.querySelector('[data-sort]');
    const offerButtons = [...root.querySelectorAll('[data-offer-filter]')];
    const mobile = matchMedia('(max-width: 700px)');
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: root.dataset.currency || 'CAD', currencyDisplay: 'code' });
    const variants = new Map(cards.map(card => [card, [...card.querySelectorAll('[data-variant]')]]));
    const preferred = new Map(cards.map(card => [card, card.querySelector('[data-variant]:checked')]));
    const colourSelect = filters.find(select => select.dataset.filter === 'colour');
    const colours = [...new Set([...variants.values()].flat().map(v => v.dataset.colour).filter(c => c !== 'Default Title'))].sort((a, b) => a.localeCompare(b));
    colours.forEach(colour => colourSelect.add(new Option(colour, colour)));
    let offerOnly = false;
    let filterOpen = false;
    let activeFilters = 0;
    let updateVideo = () => {};
    const priceFor = variant => Number(variant.dataset.offer === 'true' ? variant.dataset.salePrice : variant.dataset.price);

    // Menu links and shared URLs use the same validated options as the catalog controls.
    function readFilterURL() {
      const params = new URL(location.href).searchParams;
      filters.forEach(select => {
        const value = params.get(select.dataset.filter) || '';
        select.value = [...select.options].some(option => option.value === value) ? value : '';
      });
      const sortValue = params.get('sort') || 'default';
      sort.value = [...sort.options].some(option => option.value === sortValue) ? sortValue : 'default';
      offerOnly = offerButtons.length > 0 && params.get('offer') === 'true';
      offerButtons.forEach(button => button.setAttribute('aria-pressed', String(Boolean(button.dataset.offerFilter) === offerOnly)));
      // URL selections apply without expanding the controls.
      filterOpen = false;
    }
    function writeFilterURL() {
      const url = new URL(location.href);
      filters.forEach(select => {
        if (select.value) url.searchParams.set(select.dataset.filter, select.value);
        else url.searchParams.delete(select.dataset.filter);
      });
      if (sort.value !== 'default') url.searchParams.set('sort', sort.value);
      else url.searchParams.delete('sort');
      if (offerOnly) url.searchParams.set('offer', 'true');
      else url.searchParams.delete('offer');
      if (url.href !== location.href) history.replaceState(history.state, '', url);
    }
    readFilterURL();
    window.addEventListener('popstate', () => {
      if (!root.isConnected) return;
      readFilterURL();
      filter();
    });

    function updateCard(card, variant, changeImage = true) {
      if (!variant) return;
      variant.checked = true;
      const url = new URL(card.dataset.productUrl, location.origin);
      url.searchParams.set('variant', variant.value);
      card.querySelectorAll('[data-product-link], [data-product-link-wrap] a').forEach(link => { link.href = url.href; });
      const input = card.querySelector('[data-quick-add-variant]');
      if (input) input.value = variant.value;
      const purchase = card.querySelector('button[data-ho-action="add"], button[data-ho-action="preorder"]');
      if (purchase) {
        const available = variant.dataset.available === 'true';
        const preorder = available && variant.dataset.preorder === 'true';
        purchase.dataset.hoAction = preorder ? 'preorder' : 'add';
        purchase.disabled = !available;
        const label = purchase.querySelector('[data-ho-action-label]');
        if (label) label.textContent = !available ? purchase.dataset.hoSoldoutLabel : preorder ? purchase.dataset.hoPreorderLabel : purchase.dataset.hoAddLabel;
      }
      const img = card.querySelector('.planner-catalog__photo > img');
      if (img && changeImage && card.dataset.imageVariant !== variant.value) {
        // Keep responsive image candidates after swatch changes, especially on mobile.
        const imageURL = new URL(variant.dataset.image, location.origin);
        img.srcset = [240, 360, 540, 720].map(width => {
          imageURL.searchParams.set('width', width);
          return `${imageURL.href} ${width}w`;
        }).join(', ');
        img.src = variant.dataset.image;
        img.alt = variant.dataset.alt || variant.dataset.colour;
      }
      card.dataset.imageVariant = variant.value;
      card.querySelector('[data-selected-colour]').textContent = variant.dataset.colour;
      const onOffer = variant.dataset.offer === 'true';
      const price = priceFor(variant);
      const reference = Number(onOffer ? variant.dataset.price : variant.dataset.comparePrice);
      const current = card.querySelector('[data-current-price]');
      current.textContent = money.format(price / 100);
      current.classList.toggle('is-sale', onOffer || reference > price);
      const regular = card.querySelector('[data-regular-price]');
      regular.textContent = money.format(reference / 100);
      regular.hidden = reference <= price;
      card.querySelector('[data-offer-badge]').hidden = !onOffer;
      card.querySelector('[data-price-note]').hidden = !onOffer;
      card.querySelector('[data-availability]').textContent = variant.dataset.available !== 'true' ? 'Sold out' : variant.dataset.preorder === 'true' ? 'Pre-order' : 'In stock';
      const wishlist = card.querySelector('.wishlist-square-btn');
      if (wishlist) Object.assign(wishlist.dataset, {variantId: variant.value, variantTitle: variant.dataset.colour, variantImage: variant.dataset.image});
      updateVideo();
    }
    function showControls() {
      controls.hidden = !filterOpen;
      toggle.hidden = false;
      toggle.setAttribute('aria-expanded', String(!controls.hidden));
      toggle.textContent = `Filters${activeFilters ? ` (${activeFilters})` : ''}`;
    }
    function orderCards() {
      groups.forEach(group => {
        const grid = group.querySelector('.planner-catalog__grid');
        const ordered = [...grid.children].sort((a, b) => {
          if (sort.value === 'title') return a.dataset.title.localeCompare(b.dataset.title);
          if (sort.value === 'newest') return Number(b.dataset.created) - Number(a.dataset.created);
          if (sort.value.startsWith('price-')) {
            const delta = priceFor(a.querySelector('[data-variant]:checked')) - priceFor(b.querySelector('[data-variant]:checked'));
            return sort.value === 'price-asc' ? delta : -delta;
          }
          return Number(a.dataset.order) - Number(b.dataset.order);
        });
        ordered.forEach(card => grid.append(card));
      });
    }
    function filter(initial = false) {
      const chosen = Object.fromEntries(filters.map(select => [select.dataset.filter, select.value]));
      activeFilters = Object.values(chosen).filter(Boolean).length + Number(offerOnly);
      let count = 0;
      groups.forEach(group => { group.hidden = false; });
      cards.forEach(card => {
        const all = variants.get(card);
        const eligible = all.filter(v => (!chosen.colour || v.dataset.colour === chosen.colour)
          && (!offerOnly || v.dataset.offer === 'true')
          && (!chosen.availability || (v.dataset.available === 'true' && (chosen.availability === 'preorder') === (v.dataset.preorder === 'true'))));
        const selected = eligible.find(v => v === preferred.get(card)) || eligible[0];
        card.hidden = !selected || Boolean(chosen.cover && chosen.cover !== card.dataset.cover)
          || Boolean(chosen.size && chosen.size !== card.dataset.size)
          || Boolean(chosen.layout && !card.dataset.layout.split(' ').includes(chosen.layout));
        const restrict = Boolean(chosen.colour || offerOnly || chosen.availability);
        card.querySelector('.planner-catalog__variant-swatches').toggleAttribute('data-swatch-filtered', restrict);
        const more = card.querySelector('[data-show-more]');
        if (more) more.hidden = restrict;
        all.forEach(v => { v.closest('[data-variant-item]').hidden = restrict && !eligible.includes(v); });
        if (!card.hidden) {
          if (initial) card.dataset.imageVariant = selected.value;
          updateCard(card, selected, !initial);
          count++;
        }
      });
      groups.forEach(group => {
        const count = [...group.querySelectorAll('[data-card]')].filter(card => !card.hidden).length;
        group.hidden = count === 0;
        group.querySelector('[data-group-count]').textContent = `${count} planner${count === 1 ? '' : 's'}`;
      });
      root.querySelector('[data-count]').textContent = `${count} planner${count === 1 ? '' : 's'}${offerOnly ? ' with eligible colours' : ''}`;
      root.querySelector('[data-empty]').hidden = count !== 0;
      if (!initial || sort.value !== 'default') orderCards();
      if (!initial) writeFilterURL();
      showControls();
      updateVideo();
    }
    root.addEventListener('change', event => {
      const variant = event.target.closest('[data-variant]');
      if (variant?.checked) {
        const card = variant.closest('[data-card]');
        preferred.set(card, variant);
        updateCard(card, variant);
      } else if (event.target.matches('[data-filter]')) filter();
      else if (event.target.matches('[data-sort]')) { orderCards(); writeFilterURL(); }
    });
    offerButtons.forEach(button => button.addEventListener('click', () => {
      offerOnly = button.dataset.offerFilter === 'offer';
      offerButtons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      filter();
    }));
    root.querySelectorAll('[data-reset]').forEach(button => button.addEventListener('click', () => {
      filters.forEach(select => { select.value = ''; });
      sort.value = 'default';
      offerOnly = false;
      offerButtons.forEach(item => item.setAttribute('aria-pressed', String(!item.dataset.offerFilter)));
      filter();
    }));
    toggle.addEventListener('click', () => { filterOpen = !filterOpen; showControls(); });
    mobile.addEventListener('change', showControls);
    toolbar.querySelectorAll('nav a').forEach(link => link.addEventListener('click', event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopPropagation();
      filters.find(select => select.dataset.filter === 'cover').value = '';
      filterOpen = false;
      filter();
      // Measure after the controls collapse, before calculating the jump.
      requestAnimationFrame(() => {
        root.style.setProperty('--catalog-toolbar-height', `${toolbar.offsetHeight}px`);
        const target = root.querySelector(link.getAttribute('href'));
        if (target && !target.hidden) target.scrollIntoView({block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      });
    }));
    const resize = new ResizeObserver(() => root.style.setProperty('--catalog-toolbar-height', `${toolbar.offsetHeight}px`));
    resize.observe(toolbar);
    filter(true);

    const tile = root.querySelector('[data-promo-video]');
    const video = tile?.querySelector('video');
    if (!video) return;
    const videoCard = tile.closest('[data-card]');
    const videoToggle = videoCard.querySelector('[data-video-toggle]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let inView = false;
    let preference = 'auto';
    let attached = false;
    const playbackUI = () => {
      videoToggle.setAttribute('aria-label', video.paused ? 'Play A6 planner video' : 'Pause A6 planner video');
      videoToggle.querySelector('[data-video-pause]').hidden = video.paused;
      videoToggle.querySelector('[data-video-play]').hidden = !video.paused;
    };
    updateVideo = () => {
      const show = !videoCard.hidden && videoCard.querySelector('[data-variant]:checked').value === tile.dataset.videoVariant;
      tile.hidden = !show;
      videoToggle.hidden = !show;
      const canPlay = show && inView && !document.hidden && (preference === 'play' || (preference === 'auto' && !reduced.matches && !navigator.connection?.saveData));
      if (canPlay) {
        if (!attached) {
          const sources = [...tile.querySelector('template').content.querySelectorAll('source')];
          if (sources.length) video.append(sources[0].cloneNode(true));
          video.muted = true;
          attached = true;
          video.load();
        }
        video.play().catch(() => {});
      } else video.pause();
    };
    video.addEventListener('playing', () => { video.classList.add('is-playing'); playbackUI(); });
    video.addEventListener('pause', playbackUI);
    video.addEventListener('error', () => video.classList.remove('is-playing'));
    videoToggle.addEventListener('click', () => { preference = video.paused ? 'play' : 'pause'; updateVideo(); });
    new IntersectionObserver(entries => { inView = entries[0].isIntersecting; updateVideo(); }, {threshold: 0.25}).observe(videoCard);
    reduced.addEventListener('change', updateVideo);
    document.addEventListener('visibilitychange', updateVideo);
    updateVideo();
  }
  document.querySelectorAll('[data-planner-catalog]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-planner-catalog]').forEach(init));
})();
